<template>
  <div class="auto-suggestion-textarea" @click="hideSuggestions">
    <div ref="mirror" class="mirror"></div>

    <textarea
      ref="textarea"
      :id="id"
      :value="value"
      @input="updateValue"
      @keyup="onKeyup"
      class="form-control"
      :rows="rows"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
    ></textarea>

    <ul
      v-if="showSuggestions && filteredSuggestions.length"
      class="suggestion-list"
      :style="{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px` }"
    >
      <li
        v-for="(item, index) in filteredSuggestions"
        :key="index"
        :class="{ active: index === selectedIndex }"
        @mousedown.prevent="insertSuggestion(item)"
      >
        {{ item }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "AutoSuggestionTextarea",
  props: {
    id: {
      type: String,
      default: "auto-suggestion-textarea",
    },
    value: {
      type: String,
      default: "",
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Number,
      default: 4,
    },
    placeholder: {
      type: String,
      default: "Placeholder",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSuggestions: false,
      filteredSuggestions: [],
      dropdownPos: { top: 0, left: 0 },
      selectedIndex: 0,
    };
  },
  methods: {
    updateValue(e) {
      this.$emit("input", e.target.value);
      if (!e.target.value.includes("{{")) this.showSuggestions = false;
    },

    onKeyup(e) {
      const textarea = this.$refs.textarea;
      const cursorPos = textarea.selectionStart;
      const valueBeforeCursor = this.value.substring(0, cursorPos);
      const match = valueBeforeCursor.match(/{{\w*$/);

      if (match) {
        const keyword = match[0].replace("{{", "");
        this.filteredSuggestions = this.suggestions.filter((s) => s.toLowerCase().includes(keyword.toLowerCase()));
        this.showSuggestions = this.filteredSuggestions.length > 0;
        this.selectedIndex = 0;
        this.$nextTick(() => this.updateDropdownPosition());
      } else if (this.showSuggestions && ["Escape", "Enter", "ArrowUp", "ArrowDown"].includes(e.key)) {
        this.handleKeyNav(e);
      } else {
        this.showSuggestions = false;
      }
    },

    handleKeyNav(e) {
      if (e.key === "ArrowDown") {
        this.selectedIndex = (this.selectedIndex + 1) % this.filteredSuggestions.length;
      } else if (e.key === "ArrowUp") {
        this.selectedIndex = (this.selectedIndex - 1 + this.filteredSuggestions.length) % this.filteredSuggestions.length;
      } else if (e.key === "Enter") {
        const item = this.filteredSuggestions[this.selectedIndex];
        if (item) this.insertSuggestion(item);
      } else if (e.key === "Escape") {
        this.showSuggestions = false;
      }
    },

    updateDropdownPosition() {
      const textarea = this.$refs.textarea;
      const mirror = this.$refs.mirror;

      const style = window.getComputedStyle(textarea);
      for (const prop of [
        "fontFamily",
        "fontSize",
        "lineHeight",
        "padding",
        "border",
        "whiteSpace",
        "overflowWrap",
        "width",
      ]) {
        mirror.style[prop] = style[prop];
      }

      const cursorPos = textarea.selectionStart;
      const beforeCursor = this.value.substring(0, cursorPos);
      const safeHTML = beforeCursor.replace(/\n$/g, "\n.").replace(/\n/g, "<br/>").replace(/ /g, "&nbsp;");

      mirror.innerHTML = safeHTML + "<span id='caret-marker'>|</span>";
      const caret = mirror.querySelector("#caret-marker");
      const caretRect = caret.getBoundingClientRect();
      const textareaRect = textarea.getBoundingClientRect();

      this.dropdownPos = {
        top: caretRect.top - textareaRect.top + textarea.scrollTop + 20,
        left: caretRect.left - textareaRect.left + 8,
      };
    },

    insertSuggestion(item) {
      const textarea = this.$refs.textarea;
      const cursorPos = textarea.selectionStart;
      const before = this.value.substring(0, cursorPos);
      const after = this.value.substring(cursorPos);
      const newBefore = before.replace(/{{\w*$/, item);
      const newValue = newBefore + after;

      this.$emit("input", newValue);
      this.showSuggestions = false;

      this.$nextTick(() => {
        textarea.focus();
        const newPos = newBefore.length;
        textarea.selectionStart = textarea.selectionEnd = newPos;
      });
    },

    hideSuggestions() {
      this.showSuggestions = false;
    },
  },
};
</script>

<style scoped>
.auto-suggestion-textarea {
  position: relative;
  width: 100%;
}

.mirror {
  position: absolute;
  visibility: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

textarea.form-control {
  width: 100%;
  resize: vertical;
  font-family: monospace;
  padding: 10px;
}

.suggestion-list {
  position: absolute;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  list-style: none;
  padding: 4px 0;
  margin: 4px 0;
  width: 220px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 999;
}

.suggestion-list li {
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.1s;
}

.suggestion-list li:hover,
.suggestion-list li.active {
  background: #f0f0f0;
}
</style>
